import React, { useState, useRef, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { CalendarDays, ChevronLeft, ChevronRight } from 'lucide-react';

const DatePicker = React.forwardRef(({
  label,
  value,
  onChange,
  name,
  required,
  className = '',
  fullWidth = true,
  error,
  min,
  max,
  disabled,
  placement = 'bottom',
  align = 'left',
  ...props
}, ref) => {
  const [isOpen, setIsOpen] = useState(false);
  const [viewMode, setViewMode] = useState('days');
  const yearsRef = useRef(null);
  const triggerRef = useRef(null);
  const popupRef = useRef(null);
  const containerRef = useRef(null);
  const [popupStyle, setPopupStyle] = useState({});

  const parseValueToDate = (val) => {
    if (!val || val === '-') return null;
    const parts = val.split('-');
    if (parts.length >= 3) {
      const d = new Date(parts[0], parts[1] - 1, parts[2]);
      if (!isNaN(d.getTime())) return d;
    }
    return null;
  };

  const [currentMonth, setCurrentMonth] = useState(() => parseValueToDate(value) || new Date());

  useEffect(() => {
    const d = parseValueToDate(value);
    if (d) setCurrentMonth(d);
  }, [value]);

  // Close on outside click (trigger + popup both considered)
  useEffect(() => {
    const handleClickOutside = (e) => {
      const inTrigger = containerRef.current?.contains(e.target);
      const inPopup = popupRef.current?.contains(e.target);
      if (!inTrigger && !inPopup) setIsOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Calculate fixed popup position
  useEffect(() => {
    if (!isOpen) return;

    const updatePosition = () => {
      if (!triggerRef.current) return;
      const rect = triggerRef.current.getBoundingClientRect();
      const vw = window.innerWidth;
      const vh = window.innerHeight;
      const POPUP_W = vw < 380 ? vw - 24 : 300;
      const POPUP_H = 330;
      const GAP = 6;

      let top = rect.bottom + GAP;
      let left = rect.left;

      // On very small screens: center horizontally
      if (vw < 480) {
        left = Math.max(8, (vw - POPUP_W) / 2);
      } else {
        if (left + POPUP_W > vw - 8) left = vw - POPUP_W - 8;
        if (left < 8) left = 8;
      }

      // Flip above if not enough space below
      if (top + POPUP_H > vh - 8) {
        const flipped = rect.top - POPUP_H - GAP;
        top = flipped >= 8 ? flipped : rect.bottom + GAP;
      }

      setPopupStyle({ top, left, width: Math.min(POPUP_W, vw - 16) });
    };

    // Close calendar on outside scroll (ignore scroll inside popup, e.g. years list)
    const handleScroll = (e) => {
      if (popupRef.current?.contains(e.target)) return;
      setIsOpen(false);
    };

    updatePosition();
    window.addEventListener('scroll', handleScroll, true);
    window.addEventListener('resize', updatePosition);
    return () => {
      window.removeEventListener('scroll', handleScroll, true);
      window.removeEventListener('resize', updatePosition);
    };
  }, [isOpen]);


  // Scroll years list to selected year
  useEffect(() => {
    if (viewMode === 'years' && yearsRef.current) {
      const btn = yearsRef.current.querySelector('[data-selected="true"]');
      if (btn) {
        const c = yearsRef.current;
        c.scrollTop = btn.offsetTop - c.clientHeight / 2 + btn.clientHeight / 2;
      }
    }
  }, [viewMode]);

  useEffect(() => {
    if (!isOpen) setTimeout(() => setViewMode('days'), 200);
  }, [isOpen]);

  const getDaysInMonth = (y, m) => new Date(y, m + 1, 0).getDate();
  const getFirstDay = (y, m) => new Date(y, m, 1).getDay();

  const handleDateSelect = (day) => {
    const d = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), day);
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const dd = String(d.getDate()).padStart(2, '0');
    if (onChange) onChange({ target: { name, value: `${yyyy}-${mm}-${dd}` } });
    setIsOpen(false);
  };

  const nextMonth = () => setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 1));
  const prevMonth = () => setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, 1));

  const year = currentMonth.getFullYear();
  const month = currentMonth.getMonth();
  const daysInMonth = getDaysInMonth(year, month);
  const firstDay = getFirstDay(year, month);

  const days = [];
  for (let i = 0; i < firstDay; i++) days.push(null);
  for (let i = 1; i <= daysInMonth; i++) days.push(i);

  const MONTHS = ["January","February","March","April","May","June","July","August","September","October","November","December"];
  const DAYS = ["Su","Mo","Tu","We","Th","Fr","Sa"];

  const selectedDateObj = parseValueToDate(value);
  const isToday  = (d) => { const t = new Date(); return d === t.getDate() && month === t.getMonth() && year === t.getFullYear(); };
  const isSelected = (d) => selectedDateObj && d === selectedDateObj.getDate() && month === selectedDateObj.getMonth() && year === selectedDateObj.getFullYear();
  const isBeforeMin = (d) => { if (!min) return false; const [y,m,md] = min.split('-'); return new Date(year,month,d) < new Date(y,m-1,md); };
  const isAfterMax  = (d) => { if (!max) return false; const [y,m,md] = max.split('-'); return new Date(year,month,d) > new Date(y,m-1,md); };

  const formatDisplay = (d) => `${String(d.getDate()).padStart(2,'0')}-${String(d.getMonth()+1).padStart(2,'0')}-${d.getFullYear()}`;
  const displayValue = selectedDateObj ? formatDisplay(selectedDateObj) : 'dd-mm-yyyy';

  return (
    <div className={`flex flex-col gap-1.5 ${fullWidth ? 'w-full' : ''} ${className}`} ref={containerRef}>
      {label && (
        <label className="text-sm font-medium text-gray-700">
          {label}{required && <span className="text-red-500 ml-0.5">*</span>}
        </label>
      )}

      {/* Trigger button */}
      <div className="relative group">
        <div
          ref={triggerRef}
          onClick={() => !disabled && setIsOpen(o => !o)}
          className={[
            'w-full px-3 py-2 bg-white hover:bg-gray-50 border hover:border-gray-300',
            'rounded-lg shadow-sm text-sm transition-colors duration-200 ease-in-out',
            'cursor-pointer flex items-center justify-between min-h-[42px]',
            disabled ? 'opacity-50 cursor-not-allowed bg-gray-100 text-gray-500' : 'text-gray-900',
            error ? 'border-red-500' : 'border-gray-200',
            isOpen && !disabled ? 'ring-2 ring-blue-500/20 border-blue-500' : '',
          ].join(' ')}
        >
          <span className={value ? 'text-gray-900 font-medium text-sm' : 'text-gray-400 text-sm'}>
            {displayValue}
          </span>
          <CalendarDays size={16} className={`shrink-0 transition-colors ${isOpen ? 'text-blue-500' : 'text-gray-400 group-hover:text-gray-600'}`} />
        </div>
      </div>

      {error && <span className="text-xs font-semibold text-red-500 mt-0.5">{error}</span>}

      {/* Calendar popup — rendered via portal so no parent overflow affects it */}
      {isOpen && !disabled && ReactDOM.createPortal(
        <div
          ref={popupRef}
          style={{
            position: 'fixed',
            top: popupStyle.top,
            left: popupStyle.left,
            width: popupStyle.width || 300,
            zIndex: 9999,
          }}
          className="bg-white rounded-2xl shadow-2xl border border-gray-100 overflow-hidden animate-scale-in"
        >
          <div className="p-4">
            {/* Header */}
            <div className="flex justify-between items-center mb-2">
              <button
                onClick={prevMonth}
                type="button"
                className={`p-1.5 hover:bg-gray-100 rounded-full text-gray-500 transition-colors ${viewMode !== 'days' ? 'invisible' : ''}`}
              >
                <ChevronLeft className="w-4 h-4" />
              </button>

              <div className="flex items-center gap-0.5 font-bold text-gray-800 text-sm">
                <button
                  onClick={() => setViewMode(v => v === 'months' ? 'days' : 'months')}
                  type="button"
                  className={`px-2 py-1 rounded-lg transition-colors ${viewMode === 'months' ? 'bg-blue-50 text-blue-600' : 'hover:bg-gray-100'}`}
                >
                  {MONTHS[month]}
                </button>
                <button
                  onClick={() => setViewMode(v => v === 'years' ? 'days' : 'years')}
                  type="button"
                  className={`px-2 py-1 rounded-lg transition-colors ${viewMode === 'years' ? 'bg-blue-50 text-blue-600' : 'hover:bg-gray-100'}`}
                >
                  {year}
                </button>
              </div>

              <button
                onClick={nextMonth}
                type="button"
                className={`p-1.5 hover:bg-gray-100 rounded-full text-gray-500 transition-colors ${viewMode !== 'days' ? 'invisible' : ''}`}
              >
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>

            {/* Days view */}
            {viewMode === 'days' && (
              <>
                <div className="grid grid-cols-7 mb-1.5">
                  {DAYS.map(d => (
                    <div key={d} className="text-center text-[11px] font-semibold text-gray-400 uppercase tracking-wide py-1">{d}</div>
                  ))}
                </div>
                <div className="grid grid-cols-7 gap-y-0.5">
                  {days.map((day, idx) => {
                    if (!day) return <div key={`e-${idx}`} />;
                    const disabled_ = isBeforeMin(day) || isAfterMax(day);
                    const selected  = isSelected(day);
                    const today     = isToday(day);
                    return (
                      <button
                        key={day}
                        type="button"
                        disabled={disabled_}
                        onClick={() => handleDateSelect(day)}
                        className={[
                          'relative h-9 w-9 mx-auto rounded-lg flex items-center justify-center text-[13px] font-medium transition-all duration-150',
                          disabled_ ? 'text-gray-300 cursor-not-allowed' : 'cursor-pointer hover:bg-blue-50 hover:text-blue-600',
                          selected  ? 'bg-blue-600 text-white shadow-sm' : '',
                          !selected && today ? 'text-blue-600 font-bold' : '',
                          !selected && !today && !disabled_ ? 'text-gray-700' : '',
                        ].join(' ')}
                      >
                        {day}
                        {today && !selected && <span className="absolute bottom-1 left-1/2 -translate-x-1/2 w-1 h-1 rounded-full bg-blue-500" />}
                      </button>
                    );
                  })}
                </div>
              </>
            )}

            {/* Months view */}
            {viewMode === 'months' && (
              <div className="grid grid-cols-3 gap-1.5 py-1">
                {MONTHS.map((m, idx) => (
                  <button
                    key={m}
                    type="button"
                    onClick={() => { setCurrentMonth(new Date(year, idx, 1)); setViewMode('days'); }}
                    className={`py-2 rounded-lg text-xs font-semibold transition-all ${idx === month ? 'bg-blue-600 text-white shadow-md' : 'text-gray-700 hover:bg-blue-50 hover:text-blue-600'}`}
                  >
                    {m.substring(0, 3)}
                  </button>
                ))}
              </div>
            )}

            {/* Years view — only this inner div scrolls, not the popup */}
            {viewMode === 'years' && (
              <div
                ref={yearsRef}
                className="grid grid-cols-3 gap-1.5 py-1 h-[200px] overflow-y-auto"
                style={{ scrollbarWidth: 'thin', scrollbarColor: '#cbd5e1 transparent' }}
              >
                {Array.from({ length: 150 }, (_, i) => new Date().getFullYear() - 100 + i).map(y => (
                  <button
                    key={y}
                    type="button"
                    data-selected={y === year}
                    onClick={() => { setCurrentMonth(new Date(y, month, 1)); setViewMode('days'); }}
                    className={`py-2 rounded-lg text-xs font-semibold transition-all ${y === year ? 'bg-blue-600 text-white shadow-md' : 'text-gray-700 hover:bg-blue-50 hover:text-blue-600'}`}
                  >
                    {y}
                  </button>
                ))}
              </div>
            )}

            {/* Footer */}
            <div className="mt-2 pt-2 border-t border-gray-100 flex justify-between items-center">
              <button
                type="button"
                onClick={() => {
                  const t = new Date();
                  const fmt = `${t.getFullYear()}-${String(t.getMonth()+1).padStart(2,'0')}-${String(t.getDate()).padStart(2,'0')}`;
                  if (onChange) onChange({ target: { name, value: fmt } });
                  setCurrentMonth(new Date(t.getFullYear(), t.getMonth(), 1));
                  setIsOpen(false);
                }}
                className="text-xs font-bold text-blue-600 hover:text-blue-800 px-3 py-1.5 rounded-lg hover:bg-blue-50 transition-colors"
              >
                Today
              </button>
              <button
                type="button"
                onClick={() => { if (onChange) onChange({ target: { name, value: '' } }); setIsOpen(false); }}
                className="text-xs font-bold text-red-500 hover:text-red-700 px-3 py-1.5 rounded-lg hover:bg-red-50 transition-colors"
              >
                Clear
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
});

DatePicker.displayName = 'DatePicker';
export default DatePicker;
